import type React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCategoryDetails } from "~/api/swr/useCategoryDetails";
import { useCategories } from "~/api/swr/useCategories";
import {
  TitleAndDescriptionForm,
  type FormValues,
} from "~/molecules/TitleAndDescriptionForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useState } from "react";
import {
  createCategory,
  updateCategory,
  deleteCategory,
} from "~/api/v2/requests";

interface Props {
  categoryId?: number;
  trigger: React.ReactNode;
  onClosePopover?: () => void;
}

export const CategoryModal = ({
  categoryId,
  trigger,
  onClosePopover,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(($) => $.commons.document.category)}</DialogTitle>
        </DialogHeader>
        {categoryId ? (
          <EditCategory categoryId={categoryId} close={() => setOpen(false)} />
        ) : (
          <CreateCategory close={() => onClosePopover?.()} />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface EditCategoryProps {
  categoryId: number;
  close: () => void;
}

const EditCategory = ({ categoryId, close }: EditCategoryProps) => {
  const { t } = useTranslation();
  const { data, mutate } = useCategoryDetails(categoryId, true);
  const { mutate: mutateAll } = useCategories();

  const removeHandler = async () => {
    try {
      await deleteCategory(categoryId);
      void mutateAll();
      toast.success(t(($) => $.toasts.success.categoryRemoved));
    } catch {
      toast.error(t(($) => $.toasts.error.categoryNotRemoved));
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await updateCategory(categoryId, values);
      void mutate();
      void mutateAll();
      toast.success(t(($) => $.toasts.success.categoryUpdated));
      close();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  return (
    <TitleAndDescriptionForm
      data={data}
      onDelete={removeHandler}
      onSubmit={onSubmit}
    />
  );
};

interface CreateCategoryProps {
  close: () => void;
}

const CreateCategory = ({ close }: CreateCategoryProps) => {
  const { t } = useTranslation();
  const { mutate: mutateAllCategories } = useCategories();

  const onSubmit = async (values: FormValues) => {
    try {
      await createCategory(values);
      void mutateAllCategories();
      toast.success(t(($) => $.toasts.success.categoryCreated));
      close();
    } catch {
      toast.error(t(($) => $.toasts.error.categoryNotCreated));
    }
  };

  return <TitleAndDescriptionForm onSubmit={onSubmit} />;
};
